import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import {
  Alert,
  Button,
  Col,
  Divider,
  Dropdown,
  Flex,
  InputNumber,
  LegacyForm,
  Row,
  Select,
  Space,
  Spin,
  Upload,
  message,
  useMessagedAsync,
} from "@payping/web-components";
import styles from "@/pages/Shop/Invoice/forms/styles.module.scss";
import { Add, CuToman } from "@payping/icons";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { Product, ProductConnection } from "@/pages/Shop/Invoice/forms/type";
import CreateItemCard from "@/components/DashInvoice/CreateItemCard";
import ConfigItemModal from "@/pages/Shop/Invoice/forms/ConfigItemModal";
import CreateProductModal from "@/pages/Shop/Invoice/forms/CreateProductModal";
import { unmaskStrToNum } from "@payping/utils/string";
import type { UploadChangeParam, UploadFile } from "@payping/web-components/components/upload/types";
import { uploadApis } from "@payping/api";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import { getProductsWithImage } from "@payping/redux/actions";
import {
  ListBoolQueryParamsType,
  ListNumberQueryParamsType,
  ListStringQueryParamsType,
} from "@/pages/Shop/Product/types";
import qs from "query-string";
import { useLocation } from "react-router-dom";
import { SwitchRow } from "@/core/component";
import type { MenuProps } from "@payping/web-components/components/menu";
import { useDebounce } from "react-use";
import { numberFormatter } from "@payping/utilities-shared/number";

interface Props {
  selectedItems: Product[];
  setSelectedItems: Dispatch<SetStateAction<Product[]>>;
  calcItem: (item: Product) => Product;
  setCoupon: Dispatch<SetStateAction<number | undefined>>;
  setSumTotalAmount: Dispatch<SetStateAction<number>>;
  fileList?: UploadFile[];
  setFileList: Dispatch<SetStateAction<UploadFile[]>>;
  setTransport: Dispatch<SetStateAction<number | undefined>>;
  setTotalAmount: Dispatch<SetStateAction<number | undefined>>;
  setCountProduct: Dispatch<SetStateAction<number>>;
  showCoupon?: boolean;
  setShowCoupon: Dispatch<SetStateAction<boolean | undefined>>;
  transportationCost?: boolean;
  setTransportationCost: Dispatch<SetStateAction<boolean | undefined>>;
  loading: boolean;
  prev: () => void;
  next: () => void;
  form: FormInstance;
}
const ThirdStepInvoice: FC<Props> = ({
  selectedItems,
  setSelectedItems,
  calcItem,
  setCoupon,
  setSumTotalAmount,
  setCountProduct,
  fileList,
  setFileList,
  setTransport,
  setTotalAmount,
  showCoupon,
  setShowCoupon,
  transportationCost,
  setTransportationCost,
  loading,
  prev,
  next,
  form,
}: Props) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const items = useAppSelector((state) => state.products);
  const [currentItemConfig, setCurrentItemConfig] = useState<Product | null>(null);
  const [createProductVisible, setCreateProductVisible] = useState(false);
  const haveItemsDiscount = selectedItems?.some((item) => item.finalDiscount && item.finalDiscount > 0);
  const { searchText, searchDateFrom, searchDateTo }: ListStringQueryParamsType = qs.parse(location.search);
  const [searchValue, setSearchValue] = useState("");
  const [searchValueDebounce, setSearchValueDebounce] = useState("");
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const { pageSize = 12, pageNumber = 1 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });

  useDebounce(
    () => {
      setSearchValueDebounce(searchValue);
    },
    200,
    [searchValue],
  );

  useEffect(() => {
    const subTotal = selectedItems?.reduce((acc, item) => acc + (item.price ?? 0), 0) || 0;
    setSumTotalAmount(subTotal);
    setTotalAmount(subTotal);
    const count = selectedItems?.reduce((acc, item) => acc + Number(String(item.quantity)), 0) || 0;
    setCountProduct(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedItems), setCountProduct, setSumTotalAmount, setTotalAmount]);

  const { loading: loadingList } = useMessagedAsync(
    () =>
      dispatch(
        getProductsWithImage({
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
          searchDateFrom,
          searchDateTo,
          searchText: searchText ?? searchValueDebounce,
          withArchived: isArchived,
        }),
      ),
    [dispatch, pageSize, pageNumber, searchText, isArchived, searchDateFrom, searchDateTo, searchValueDebounce],
    { success: false, loading: false },
  );

  const onFinish = () => {
    next();
  };

  const itemButton: MenuProps["items"] = [
    { key: 0, label: "میزان تخفیف", onClick: () => setShowCoupon(true) },
    { key: 1, label: "هزینه حمل و نقل", onClick: () => setTransportationCost(true) },
  ];
  return (
    <LegacyForm<ProductConnection>
      canDeactivate
      scrollToFirstError={{ behavior: "smooth" }}
      form={form}
      layout={"vertical"}
      onFinish={onFinish}
      onValuesChange={(changedValues, values) => {
        if ("discountAmount" in changedValues || "discountType" in changedValues || "shipping" in changedValues) {
          const subTotal = selectedItems?.reduce((acc, item) => acc + (item.price ?? 0), 0) || 0;
          let totalDiscountAmount = selectedItems?.reduce((acc, item) => acc + (item.finalDiscount ?? 0), 0) || 0;
          let total: number;
          let discountAmount = unmaskStrToNum(values.discountAmount || "");
          const { discountType } = values;

          if (discountAmount) {
            if (discountAmount < 0) discountAmount = 0;
            if (discountType === 1) {
              totalDiscountAmount = discountAmount;
            }

            if (discountType === 0) {
              if (discountAmount > 100) discountAmount = 100;
              totalDiscountAmount = (subTotal * discountAmount) / 100;
            }
          }

          total = subTotal;
          setTotalAmount(total);
          total -= totalDiscountAmount;
          if (total < 0) total = 0;

          const shipping = Number(values.shipping ?? 0);

          if (shipping) {
            total += shipping;
          }
          setTransport(shipping);

          setCoupon(totalDiscountAmount);
          setTotalAmount(total);
        }
      }}
    >
      <LegacyForm.Item>
        <Select
          placeholder={t("invoice.create.base.searchAndSelect")}
          className="tw:w-full"
          value={null}
          onChange={(value: string) => {
            const item = items.list.find((listItem) => listItem.code === value);
            if (item) {
              setSelectedItems([...(selectedItems || []), calcItem({ ...item, quantity: 1 })]);
              if (item.defineAmountByUser) {
                message.warning({
                  content: t("invoice.create.base.setPrice"),
                  duration: 3,
                });
                setCurrentItemConfig(item);
              }
            }
          }}
          showSearch
          onSearch={(value) => {
            setSearchValue(value);
          }}
          filterOption={(input, option) =>
            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          popupRender={(menu) => {
            if (loading || loadingList) {
              return (
                <div className="tw:flex tw:w-full tw:items-center tw:justify-center tw:p-4!">
                  <Spin />
                </div>
              );
            }
            return (
              <div>
                {menu}
                <Divider className={styles.divider} />
                <div onClick={() => setCreateProductVisible(true)}>
                  <a className={styles.anchor}>
                    <Add width={14} />
                    <span className={styles.mr5}>{t("invoice.create.base.addItem")}</span>
                  </a>
                </div>
              </div>
            );
          }}
        >
          {items.list.map((item, index) => (
            <Select.Option
              value={item.code}
              key={index}
              disabled={selectedItems.some((selectedItem) => selectedItem.code === item.code)}
            >
              {item.title ?? ""}
            </Select.Option>
          ))}
        </Select>
      </LegacyForm.Item>
      <LegacyForm.Item>
        <Flex vertical gap="small" className="tw:w-full tw:sm:w-1/2">
          {selectedItems?.map((item) => (
            <CreateItemCard
              key={item.code}
              item={item}
              onEdit={(_item) => setCurrentItemConfig(_item)}
              onRemove={() => setSelectedItems(selectedItems.filter((_item) => _item.code !== item.code))}
            />
          ))}
        </Flex>
      </LegacyForm.Item>
      <LegacyForm.Item label={"اضافه کردن هزینه"}>
        <Dropdown placement={"bottomRight"} menu={{ items: itemButton }}>
          <Button icon={<Add width={14} height={14} />}>{"افزودن"}</Button>
        </Dropdown>
      </LegacyForm.Item>
      {showCoupon && (
        <LegacyForm.Item label={t("invoice.create.base.discount")} shouldUpdate={() => true}>
          {({ getFieldValue }) => (
            <Flex flex={1}>
              <Space.Compact>
                <LegacyForm.Item
                  noStyle
                  name="discountAmount"
                  initialValue={0}
                  rules={[
                    getFieldValue("discountType") === 0
                      ? { max: 99, min: 0, transform: (value): number => Number(value), type: "number" }
                      : { transform: (value): number => Number(value), type: "number" },
                  ]}
                >
                  <InputNumber
                    formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                    parser={(value) => numberFormatter.parse(value)}
                    disabled={loading}
                    suffix={getFieldValue("discountType") === 0 ? "%" : <CuToman width="20px" height="20px" />}
                    className="tw:w-full!"
                    inputMode="numeric"
                    changeOnWheel={false}
                    controls={false}
                  />
                </LegacyForm.Item>
                <LegacyForm.Item noStyle name="discountType" initialValue={0}>
                  <Select disabled={haveItemsDiscount || loading}>
                    <Select.Option value={0}>{t("invoice.create.base.percent")}</Select.Option>
                    <Select.Option value={1}>{t("invoice.create.base.amount")}</Select.Option>
                  </Select>
                </LegacyForm.Item>
              </Space.Compact>
              {haveItemsDiscount && <Alert title="" description={t("invoice.create.base.moreDiscount")} type="info" />}
            </Flex>
          )}
        </LegacyForm.Item>
      )}
      {transportationCost && (
        <LegacyForm.Item label={t("invoice.create.base.transportationCost")} name={"shipping"} initialValue={0}>
          <InputNumber
            formatter={(value) => numberFormatter.format(value, { maxDecimals: 2 })}
            parser={(value) => numberFormatter.parse(value)}
            disabled={loading}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
          />
        </LegacyForm.Item>
      )}
      <LegacyForm.Item label={"افزودن فایل"} name={"attachments"} shouldUpdate={() => true}>
        <Upload
          disabled={loading}
          listType={"picture-card"}
          onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
          fileList={fileList}
          crop={false}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          allowedExtensions={["jpg", "jpeg", "png", "pdf", "xls", "zip", "rar"]}
          requestFunction={uploadApis.invoiceAttachmentWithFilePost}
          showTooltip
        >
          <div>
            <Add width={"14px"} className={"color--primary"} />
            <div className={"ant-upload-text"}>{t("invoice.form.upload.label")}</div>
          </div>
        </Upload>
      </LegacyForm.Item>
      <LegacyForm.Item name={"sendAttachmentsAfterSuccessPayment"} valuePropName={"checked"}>
        <SwitchRow label={"نمایش فایل‌ها فقط پس از پرداخت"} loading={loading} />
      </LegacyForm.Item>
      <LegacyForm.Item>
        <Row gutter={[8, 8]}>
          <Col flex={2}>
            <Button block type={"primary"} htmlType={"submit"}>
              {t("shop.createInvoice.nextStep")}
            </Button>
          </Col>
          <Col flex={1}>
            <Button block onClick={() => prev()}>
              {t("back")}
            </Button>
          </Col>
        </Row>
      </LegacyForm.Item>
      {currentItemConfig && (
        <ConfigItemModal
          maxQuantity={items.list.find((_item) => _item.code === currentItemConfig?.code)?.quantity}
          item={currentItemConfig}
          onCancel={() => setCurrentItemConfig(null)}
          onOk={(updatedItem) => {
            setSelectedItems(
              selectedItems?.map((item) => (item.code === updatedItem.code ? calcItem(updatedItem) : item)),
            );
            setCurrentItemConfig(null);
          }}
        />
      )}
      <CreateProductModal
        visible={createProductVisible}
        onCancel={() => setCreateProductVisible(false)}
        onOk={async (val) => {
          const v = await calcItem(val);
          setSelectedItems((prevState) => [...prevState, v]);
          setCreateProductVisible(false);
        }}
      />
    </LegacyForm>
  );
};

export default ThirdStepInvoice;
